import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Dialog, Toast } from "antd-mobile";
import { toastSuccess, toastWarning } from "@/utils/toast";
import { uploadImage } from "@/api/upload";
import { getStaffCateItemDetail, updateStaffCateItem } from "@/api/staff";
import type { StaffCateItemDetail } from "@/api/staff";

const BRAND = "#FF6B35";

// 统一 image 字段（兼容旧版 images 数组）
function normalizeDetailImage(
  data: StaffCateItemDetail & { images?: unknown },
): string {
  if (typeof data.image === "string" && data.image.trim())
    return data.image.trim();
  const legacy = data.images;
  if (Array.isArray(legacy) && legacy.length > 0) {
    const first = legacy[0];
    return typeof first === "string" ? first : String(first ?? "");
  }
  if (typeof legacy === "string" && legacy.trim()) return legacy.trim();
  return "";
}

// 单价须为有效数字且严格大于 0
const isPositivePrice = (v: string | number | undefined): boolean => {
  if (v === undefined || v === null || v === "") return false;
  const n = parseFloat(String(v).trim());
  return !Number.isNaN(n) && n > 0;
};

export default function EditItemDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const cateId =
    Number((location.state as { cateId?: number })?.cateId) ||
    Number(new URLSearchParams(location.search).get("cateId")) ||
    -1;

  const [detail, setDetail] = useState<StaffCateItemDetail | null>(null);
  const [formData, setFormData] = useState<{
    id: number;
    accept_order_type: number;
    price: string;
    day_order_money: string;
    image: string;
    number: string;
  }>({
    id: -1,
    accept_order_type: 0,
    price: "",
    day_order_money: "",
    image: "",
    number: "",
  });
  const [imgLists, setImgLists] = useState<{ url: string; name: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cateId <= 0) return;
    getStaffCateItemDetail({ id: cateId })
      .then((d) => {
        setDetail(d);
        setFormData({
          id: d.id,
          accept_order_type: d.accept_order_type ?? 0,
          price: String(d.price ?? ""),
          day_order_money: String(d.day_order_money ?? ""),
          image: normalizeDetailImage(d),
          number:
            d.number != null && d.number !== "" ? String(d.number).trim() : "",
        });
        const imgUrl = normalizeDetailImage(d);
        if (imgUrl) setImgLists([{ url: imgUrl, name: "qualification" }]);
      })
      .catch(console.error);
  }, [cateId]);

  const onHourPriceBlur = () => {
    const v = String(formData.price ?? "").trim();
    if (v === "") return;
    if (!isPositivePrice(v)) {
      setFormData((prev) => ({ ...prev, price: "" }));
      toastWarning("小时单价须大于0");
    }
  };

  const onDayPriceBlur = () => {
    const v = String(formData.day_order_money ?? "").trim();
    if (v === "") return;
    if (!isPositivePrice(v)) {
      setFormData((prev) => ({ ...prev, day_order_money: "" }));
      toastWarning("日单价须大于0");
    }
  };

  // ── 资质图片选择上传 ──
  const handleQualificationSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const file = files[0];
      const localUrl = URL.createObjectURL(file);
      setImgLists([{ url: localUrl, name: file.name }]);

      // 上传到服务器
      const url = await uploadImage(file);
      setFormData((prev) => ({ ...prev, image: url }));
      setImgLists([{ url, name: "qualification" }]);
    } catch (err) {
      console.error("[EditItemDetail] upload failed:", err);
      toastWarning("上传失败");
      setFormData((prev) => ({ ...prev, image: "" }));
      setImgLists([]);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onQualificationDelete = () => {
    setFormData((prev) => ({ ...prev, image: "" }));
    setImgLists([]);
  };

  // ── 提交申请 ──
  const handleSubmit = () => {
    // 小时单价校验
    if (formData.accept_order_type === 0 || formData.accept_order_type === 1) {
      if (!formData.price) return toastWarning("请填写小时单价");
      if (!isPositivePrice(formData.price)) return toastWarning("小时单价须大于0");
    }
    // 日单价校验
    if (formData.accept_order_type === 0 || formData.accept_order_type === 2) {
      if (!formData.day_order_money) return toastWarning("请填写日单价");
      if (!isPositivePrice(formData.day_order_money)) return toastWarning("日单价须大于0");
    }
    // 资质校验
    if (detail?.is_qualification) {
      if (!String(formData.image || "").trim()) return toastWarning("请上传资质（1 张）");
      if (!String(formData.number || "").trim()) return toastWarning("请填写证书编号");
    }

    Dialog.show({
      content: "是否确认提交申请？",
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认提交",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              try {
                Toast.show({ icon: "loading", content: "提交中..." });
                const fd = new FormData();
                fd.append("id", String(formData.id));
                fd.append("accept_order_type", String(formData.accept_order_type));
                fd.append("price", formData.price);
                fd.append("day_order_money", formData.day_order_money);
                fd.append("image", formData.image);
                fd.append("number", formData.number);
                await updateStaffCateItem(fd);
                toastSuccess("提交成功");
                setTimeout(() => navigate(-1), 800);
              } catch {/* 错误已在 request 层统一 toast */}
            },
          },
        ],
      ],
    });
  };

  // Loading 状态
  if (!detail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <i className="ri-loader-4-line text-2xl animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-between bg-gray-50">
      {/* Header — 品牌橙色 */}
      <div style={{ background: BRAND }} className="flex-shrink-0">
        <div
          className="px-5 flex items-center gap-3"
          style={{
            paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)",
            paddingBottom: 13,
          }}
        >
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i
              className="ri-arrow-left-line"
              style={{ fontSize: 20, color: "#fff" }}
            />
          </button>
          <span className="text-lg font-bold text-white">项目详情</span>
        </div>
      </div>

      <div className="p-[11px] flex-1 flex flex-col gap-[11px]">
        {/* 项目信息卡片 */}
        <div className="px-[14px] py-[18px] rounded-2xl bg-white flex gap-[14px]">
          <img
            src={detail.thumb || ""}
            alt=""
            className="w-[50px] h-[50px] rounded-full object-cover shrink-0 bg-gray-100"
          />
          <div className="flex flex-col gap-[5px]">
            <span className="text-sm font-bold text-gray-900">
              {detail.title}
            </span>
            <p className="text-xs text-gray-400 leading-relaxed line-clamp-6">
              {detail.short_title}
            </p>
          </div>
        </div>

        {/* 表单填写 — 价格设置 */}
        <div className="px-[14px] py-[18px] bg-white rounded-2xl space-y-4">
          {(formData.accept_order_type === 1 ||
            formData.accept_order_type === 0) && (
            <label className="flex items-center gap-3">
              <span className="text-sm text-gray-700 w-[80px] shrink-0">
                小时单价
              </span>
              <input
                type="number"
                value={formData.price}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, price: e.target.value }))
                }
                onBlur={onHourPriceBlur}
                placeholder="请输入小时单价（须大于0）"
                className="flex-1 py-2 px-3 bg-transparent outline-none text-sm font-medium text-right"
              />
            </label>
          )}

          {(formData.accept_order_type === 0 ||
            formData.accept_order_type === 2) && (
            <label className="flex items-center gap-3">
              <span className="text-sm text-gray-700 w-[80px] shrink-0">
                日单价
              </span>
              <input
                type="number"
                value={formData.day_order_money}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    day_order_money: e.target.value,
                  }))
                }
                onBlur={onDayPriceBlur}
                placeholder="请输入日单价（须大于0）"
                className="flex-1 py-2 px-3 bg-transparent outline-none text-sm font-medium text-right"
              />
            </label>
          )}
        </div>

        {/* 证书编号 + 资质上传 同一卡片 */}
        <div className="px-[14px] py-[18px] bg-white rounded-2xl flex flex-col gap-[14px]">
          <span className="text-sm font-bold text-gray-900">证书与资质</span>

          <label className="flex items-center gap-4">
            <span className="w-[70px] shrink-0 text-sm text-gray-700">
              证书编号
            </span>
            <input
              value={formData.number}
              onChange={(e) =>
                setFormData((p) => ({ ...p, number: e.target.value }))
              }
              type="text"
              placeholder="需资质时必填"
              maxLength={100}
              className="flex-1 py-2 px-3 bg-transparent outline-none text-sm font-medium text-right"
            />
          </label>

          {/* 资质图片上传（限 1 张） */}
          <div className="flex flex-col gap-[8px]">
            <span className="text-sm font-medium text-gray-700">
              资质图片（限 1 张）
            </span>

            {imgLists.length > 0 ? (
              <div className="relative w-[100px] h-[100px] rounded-[15px] overflow-hidden border border-gray-100">
                <img
                  src={imgLists[0].url}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={onQualificationDelete}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 flex items-center justify-center cursor-pointer active:scale-[0.95]"
                >
                  <i className="ri-close-line text-white text-xs" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-[100px] h-[100px] rounded-[15px] border border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer active:bg-orange-50 transition-colors"
              >
                <i className="ri-image-add-line text-xl text-gray-400" />
                <span className="text-xs text-gray-400 mt-1">点击上传</span>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleQualificationSelect}
            />
          </div>
        </div>
      </div>

      {/* 底部提交按钮 */}
      <div className="flex-shrink-0 p-[11px] bg-gray-50">
        <button
          type="button"
          onClick={handleSubmit}
          className="w-full py-3 rounded-2xl text-white text-base font-semibold active:opacity-90 transition-opacity cursor-pointer shadow-lg"
          style={{ background: `linear-gradient(135deg, ${BRAND}, #ff8c5a)` }}
        >
          提交申请
        </button>
      </div>
    </div>
  );
}
